"use client";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import { IRoleOrganizationUser, OrganizationUser } from "@/models/organization";
import { User } from "@/models/user";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, MenuItem, Popover, Typography } from "@mui/material";
import clsx from "clsx";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
interface LoginButtonProps {
  isLogin: boolean;
  btnLogout: () => void;
  handleClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  user?: User | null;
  anchorEl: any;
  setAnchorEl: (value: any) => void;
  open: boolean;
  handleClose: () => void;
  yourReports: any[];
  toggleDrawer?: (value: boolean) => void;
  organization?: OrganizationUser;
  locale?: string;
}
const LoginButton = ({
  isLogin,
  btnLogout,
  handleClick,
  user,
  anchorEl,
  handleClose,
  open,
  yourReports,
  setAnchorEl,
  toggleDrawer,
  organization,
  locale,
}: LoginButtonProps) => {
  const [currentUrl, setcurrentUrl] = useState<string>("");
  const pathName = usePathname();
  const t = useTranslations("messages");
  const yourAcount = useMemo(
    () => [
      {
        icon: image.iconPerson,
        name: t("common_profile"),
        url: APP_ROUTES(locale as any).ACCOUNT.PROFILE.to,
      },
      {
        icon: image.iconSubscription,
        name: t("common_subscription"),
        url: APP_ROUTES(locale as any).ACCOUNT.SUBSCRIPTION.to,
      },
      ...(user?.organization?.role === IRoleOrganizationUser.MEMBER
        ? []
        : [
          {
            icon: image.iconTeam,
            name: t("account_page_tab_organisation"),
            url: APP_ROUTES(locale as any).ACCOUNT.ORGANISATION.to,
          },
        ]),
      {
        icon: image.iconLock,
        name: t("common_password_and_security"),
        url: APP_ROUTES(locale as any).ACCOUNT.PASSWORD_SECURITY.to,
      },
      {
        icon: image.iconTranslate,
        name: t("common_language"),
        url: APP_ROUTES(locale as any).ACCOUNT.LANGUAGE.to,
      },
    ],
    [t, user, locale]
  );
  useEffect(() => {
    const handlePathChange = () => {
      const path =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      setcurrentUrl(path);
    };
    handlePathChange();
  }, [pathName]);

  const handleRedirect = () => {
    setAnchorEl(null);
    if (toggleDrawer) {
      toggleDrawer(false);
    }
  };

  if (user) {
    return (
      <>
        <Box className="boxAvatar">
          <motion.button
            className="flex justify-between p-0 gap-2 border-0 bg-white itemBtn cursor-pointer hover:bg-slate-100"
            onClick={handleClick}
          >
            <div className="relative h-10 w-10  flex items-center justify-center">
              {user?.license && user?.license.isTrial && (
                <span
                  className={clsx(
                    "absolute z-10 px-1.5 py-0.5 rounded-full bg-yellow-300 text-yellow-900 text-[10px] font-semibold shadow pointer-events-none select-none",
                    {
                      "top-0 -right-[1.25rem] translate-x-1/3 -translate-y-1/3 min-w-[65px]":
                        locale === "vi",
                      "top-0 right-0 translate-x-1/3 -translate-y-1/3":
                        locale === "en",
                    }
                  )}
                  title={t("account_subscription_trial")}
                  translate-key="account_subscription_trial"
                >
                  {t("account_subscription_trial")}
                </span>
              )}
              {user?.license && !user?.license.isOutOfDate && (
                <div className="absolute inset-0 rounded-full bg-gradient-to-b from-[#0B78BD] to-[#C7F129]"></div>
              )}
              <div className="relative h-[34px] w-[34px] rounded-full bg-gradient-to-tr from-[#e0eafc] to-[#cfdef3] shadow-md flex items-center justify-center overflow-hidden !border-1 border-white">
                <Image
                  priority
                  quality={100}
                  alt="iconProfile"
                  src={user?.avatar ?? image.iconProfile}
                  className="rounded-full object-cover transition-transform duration-300 hover:scale-105"
                  width={30}
                  height={30}
                />
              </div>
            </div>
            <div className="gap-1 flex justify-between items-center">
              <span className="flex sm:hidden">
                {user && `${user?.firstName}`}
              </span>
              <span
                className={`hidden sm:flex capitalize text-xs/[24px] text-gray_90 font-normal`}
              >
                {user &&
                  (user?.fullName ?? `${user?.firstName ? user?.firstName + " " : ""} ${user?.lastName ? user?.lastName : ""}`)}
              </span>
              <KeyboardArrowDownIcon
                className={`text-gray_60 !w-4 !h-[15.60px] ${anchorEl && "rotate-180"}`}
              />
            </div>
          </motion.button>
        </Box>
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          disableScrollLock={true}
          sx={{
            ".MuiList-root": {
              padding: 0,
            },
            "& .MuiPopover-paper": {
              marginLeft: "-6px", // Offset theo chiều ngang
            },
          }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                paddingY: "3px",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              },
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            component="div"
            sx={{
              pointerEvents: "none",
              cursor: "text",
              userSelect: "text",
              "&:hover": {
                backgroundColor: "transparent", // Bỏ hiệu ứng hover
              },
            }}
            className="!font-medium !text-sm/6 bg-gray_2 !text-gray_60 !py-[15px]"
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ whiteSpace: "nowrap" }}>{user.email}</span>

              {organization?.organization?.name && (
                <span
                  style={{
                    fontSize: "13px",
                    color: "#888",
                    whiteSpace: "nowrap",
                  }}
                >
                  {organization.organization.name}
                </span>
              )}
            </div>
          </MenuItem>
          {yourAcount.map((item, index) => (
            <Link
              onClick={() => handleRedirect()}
              key={index}
              className="no-underline text-gray-80"
              href={item.url}
            >
              <MenuItem className="!py-2">
                <Box className="gap-3 flex items-center">
                  <Image
                    src={item.icon}
                    alt={item.icon}
                    width={20}
                    height={20}
                  />
                  <span className="flex items-end h-full text-sm/6 font-normal">
                    {item.name}
                  </span>
                </Box>
              </MenuItem>
            </Link>
          ))}
          <MenuItem
            className="!font-medium !text-sm/6 bg-gray_2 !text-gray_60 h-10"
            sx={{
              cursor: "default",
            }}
            translation-key="common_your_reports"
          >
            {t("common_your_reports")}
          </MenuItem>
          {yourReports.map((item, index) => (
            <Link
              onClick={() => handleRedirect()}
              key={index}
              className="no-underline text-gray-80"
              href={item.url}
            >
              <MenuItem className="!py-2">
                <Box className="gap-3 flex items-center">
                  <Image
                    src={item.icon}
                    alt={item.icon}
                    width={20}
                    height={20}
                  />
                  <span className="flex items-end h-full text-sm/6 font-normal">
                    {item.name}
                  </span>
                </Box>
              </MenuItem>
            </Link>
          ))}
          <MenuItem
            className="font-medium h-10 text-sm/6 cimigo_danger !border-t !border-solid !border-[#D2D2D2] !pt-[4px]"
            onClick={() => {
              btnLogout();
            }}
          >
            <Image src={image.iconLogout} alt="iconLogout" className="me-2" />
            <span
              translation-key="common_log_out"
              className="flex items-center text-cimigo_danger text-sm/6 font-normal"
            >
              {t("common_log_out")}
            </span>
          </MenuItem>
        </Popover>
      </>
    );
  }
  if (!isLogin) {
    return (
      <div className="hidden md:flex">
        <Link
          href={`${APP_ROUTES().LOGIN.to}?redirect=${encodeURIComponent(currentUrl)}`}
          className={
            "!bg-ciBlue_light_4 !py-2 !px-4 hover:cursor-pointe !rounded btn_login no-underline appbar-login-link"
          }
        >
          <Typography
            className={
              "!font-medium normal-case !text-sm/[24px] text-cimigo-blue"
            }
            translation-key="common_login"
          >
            {t("common_login")}
          </Typography>
        </Link>
      </div>
    );
  }
};

export default LoginButton;
